/**
 * The workflow's Needs review tab: every open blocking gap, conflict, gap and
 * omitted section across the deals' latest memos, grouped by deal, each
 * linking into the run's own Needs you tab (id "gaps") where it is resolved.
 *
 * The items are derived from each run's facts by `reviewItems` — the same
 * function the run page lists from — so the two tabs cannot disagree about
 * what needs a person.
 */

import { ChevronRight } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

import type { DealScreeningRun } from "../../types/dealScreening";
import type { PerDealResult } from "../../hooks/useDealScreeningPerDeal";
import { runHref } from "./DealsTable";
import { memoVersionText } from "./memoVersionLabel";
import { type ReviewItem, reviewItems } from "./dealScreeningReview";
import ReviewKindPill from "./ReviewKindPill";

export interface DealReviewGroup {
	dealName: string;
	run: DealScreeningRun;
	items: ReviewItem[];
}

/** Deals with something open, in board order. */
export const reviewGroups = (
	results: PerDealResult<DealScreeningRun>[],
): DealReviewGroup[] =>
	results
		.map(({ deal, value }) => ({
			dealName: deal.name,
			run: value,
			items: reviewItems(value),
		}))
		.filter((group) => group.items.length > 0);

export interface DealsNeedsReviewProps {
	groups: DealReviewGroup[];
	loading: boolean;
	error: string | null;
}

const DealsNeedsReview: React.FC<DealsNeedsReviewProps> = ({
	groups,
	loading,
	error,
}) => (
	<div className="space-y-4">
		{error && (
			<p role="alert" className="text-sm text-red-600">
				{error}
			</p>
		)}
		{groups.length === 0 ? (
			<p className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-text-muted">
				{loading ? "Reading the memos…" : "No gaps open on any memo."}
			</p>
		) : (
			groups.map((group) => (
				<section
					key={group.run.public_id}
					className="rounded-xl border border-gray-200 bg-white"
					aria-label={group.dealName}
				>
					<header className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
						<h2 className="text-base font-medium text-text-base">
							{group.dealName}{" "}
							<span className="text-sm font-normal text-text-muted">
								· {memoVersionText(group.run)}
							</span>
						</h2>
						<Link
							to={runHref(group.run.public_id, "gaps", "review")}
							className="text-sm font-medium text-[#008782] hover:underline"
						>
							Open in memo
						</Link>
					</header>
					<ul>
						{group.items.map((item) => (
							<li key={item.id} className="border-b border-gray-100 last:border-0">
								<Link
									to={runHref(group.run.public_id, "gaps", "review")}
									className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50"
								>
									<ReviewKindPill kind={item.kind} />
									<span className="min-w-0 flex-1">
										<span className="block text-base text-text-base">
											{item.title}
										</span>
										{item.question && (
											<span className="block text-sm text-text-muted">
												{item.question.text}
											</span>
										)}
									</span>
									<ChevronRight
										className="mt-0.5 h-4 w-4 shrink-0 text-text-muted"
										aria-hidden
									/>
								</Link>
							</li>
						))}
					</ul>
				</section>
			))
		)}
	</div>
);

export default DealsNeedsReview;
